import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Globe, ExternalLink, Info, BookOpen, Package } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { TariffResultsTable } from '../components/TariffResultsTable';
import { CarmJsonViewer } from '../components/CarmJsonViewer';
import { TariffAiHelper } from '../components/TariffAiHelper';
import { useTariffs } from '../hooks';
import type { TariffResource, TariffFilters } from '../types';

const ATEK_PRODUCTS = [
  { productKey: 'pcb', supplier: 'Elecrow', origin: 'China', hsCode: '8534.00.00.19', duty: 'FREE' },
  { productKey: 'batteries', supplier: 'VoltaPlex', origin: 'China', hsCode: '8507.60.90.00', duty: 'FREE' },
  { productKey: 'enclosures', supplier: 'Polycase', origin: 'US', hsCode: '3926.90.99.90', duty: 'FREE (CUSMA)' },
  { productKey: 'sensors', supplier: 'JY Sensors', origin: 'China', hsCode: '9025.19.00.90', duty: 'FREE' },
  { productKey: 'cables', supplier: 'Cable Tech', origin: 'China', hsCode: '8544.42.00.90', duty: 'FREE' },
  { productKey: 'gateways', supplier: 'Dragino', origin: 'China', hsCode: '8517.62.00.90', duty: 'FREE' },
  { productKey: 'transmitters', supplier: 'Milesight', origin: 'China', hsCode: '8517.62.00.90', duty: 'FREE' },
];

const TREATMENT_ROWS = [
  { code: '007', abbr: 'MFN', name: 'Most Favoured Nation', highlight: true },
  { code: '014', abbr: 'UST', name: 'Canada-US (CUSMA)', highlight: true },
  { code: '013', abbr: 'MXT', name: 'Canada-Mexico (CUSMA)' },
  { code: '003', abbr: 'General', name: 'General Tariff' },
  { code: '025', abbr: 'CEUT', name: 'CETA (European Union)' },
  { code: '021', abbr: 'CPTPP', name: 'Trans-Pacific Partnership' },
  { code: '031', abbr: 'UKFTA', name: 'Canada-UK FTA' },
  { code: '033', abbr: 'CUFTA', name: 'Canada-Ukraine FTA' },
  { code: '029', abbr: 'CKFTA', name: 'Canada-Korea FTA' },
  { code: '011', abbr: 'GPT', name: 'General Preferential Tariff' },
  { code: '009', abbr: 'AUT', name: 'Australia FTA' },
  { code: '027', abbr: 'CT', name: 'Chile FTA' },
  { code: '028', abbr: 'CHFTA', name: 'Canada-Honduras FTA' },
  { code: '030', abbr: 'CPFTA', name: 'Canada-Panama FTA' },
  { code: '002', abbr: 'BPT', name: 'British Preferential' },
];

const GLOSSARY_KEYS = [
  'hsCode', 'tariffClassification', 'customsDuties', 'gst', 'exciseTax', 'exciseDuty',
  'mfn', 'cusma', 'gpt', 'vfd', 'cad', 'adValorem', 'uom', 'bond',
];

export function CarmPage() {
  const { t, i18n } = useTranslation('carm');
  const lang = i18n.language === 'fr' ? 'fr' : 'en';

  const [resource, setResource] = useState<TariffResource>('tariffClassifications');
  const [tariffNumber, setTariffNumber] = useState('');
  const [description, setDescription] = useState('');
  const [top, setTop] = useState('50');

  const [activeFilters, setActiveFilters] = useState<TariffFilters>({});
  const [activeResource, setActiveResource] = useState<TariffResource>('tariffClassifications');
  const [hasSearched, setHasSearched] = useState(false);

  const { data, isLoading, error } = useTariffs(activeResource, activeFilters, hasSearched);

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    const filters: TariffFilters = { lang, $top: top };
    if (tariffNumber.trim()) filters.tariffNumber = tariffNumber.trim();
    if (description.trim()) filters.description = description.trim();
    setActiveFilters(filters);
    setActiveResource(resource);
    setHasSearched(true);
  };

  const handleQuickSearch = (hsCode: string) => {
    setTariffNumber(hsCode);
    setResource('tariffClassifications');
    const filters: TariffFilters = { lang, $top: top, tariffNumber: hsCode };
    setActiveFilters(filters);
    setActiveResource('tariffClassifications');
    setHasSearched(true);
  };

  const handleAiSuggestion = (suggestion: { tariffNumber?: string }) => {
    if (suggestion.tariffNumber) {
      setTariffNumber(suggestion.tariffNumber);
      setResource('tariffClassifications');
      const filters: TariffFilters = { lang, $top: top, tariffNumber: suggestion.tariffNumber };
      setActiveFilters(filters);
      setActiveResource('tariffClassifications');
      setHasSearched(true);
    }
  };

  const portalUrl = lang === 'fr'
    ? 'https://ccp-pcc.cbsa-asfc.cloud-nuage.canada.ca/fr/auth/login'
    : 'https://ccp-pcc.cbsa-asfc.cloud-nuage.canada.ca/en/auth/login';

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-muted-foreground" />
            <h1 className="text-2xl font-bold tracking-tight">{t('page.title')}</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{t('page.subtitle')}</p>
        </div>
        <a href={portalUrl} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border bg-background hover:bg-muted transition-colors">
          <ExternalLink className="h-3.5 w-3.5" />
          {t('page.portalLogin')}
        </a>
      </div>

      {/* AI Helper */}
      <TariffAiHelper onSuggestion={handleAiSuggestion} />

      {/* ATEK Products */}
      <div className="border rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 bg-muted/50 border-b">
          <Package className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-xs font-semibold">{t('products.title')}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left px-4 py-2 font-medium">{t('products.headers.product')}</th>
                <th className="text-left px-4 py-2 font-medium">{t('products.headers.supplier')}</th>
                <th className="text-left px-4 py-2 font-medium">{t('products.headers.origin')}</th>
                <th className="text-left px-4 py-2 font-medium">{t('products.headers.hsCode')}</th>
                <th className="text-left px-4 py-2 font-medium">{t('products.headers.duty')}</th>
                <th className="text-left px-4 py-2 font-medium">{t('products.headers.gst')}</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {ATEK_PRODUCTS.map((p) => (
                <tr key={`${p.hsCode}-${p.supplier}`} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-2 font-medium">{t(`products.items.${p.productKey}`)}</td>
                  <td className="px-4 py-2 text-muted-foreground">{p.supplier}</td>
                  <td className="px-4 py-2 text-muted-foreground">{p.origin}</td>
                  <td className="px-4 py-2 font-mono">{p.hsCode}</td>
                  <td className="px-4 py-2">
                    <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 text-[10px] font-medium">{p.duty}</span>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">5%</td>
                  <td className="px-4 py-2">
                    <button type="button" onClick={() => handleQuickSearch(p.hsCode)}
                      className="px-2 py-1 text-[10px] rounded border hover:bg-muted transition-colors">
                      <Search className="h-3 w-3 inline mr-0.5" />{t('products.lookup')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex items-end gap-3 flex-wrap">
        <div className="space-y-1 min-w-[180px] max-w-[220px]">
          <Label className="text-xs">{t('search.tariffNumber')}</Label>
          <Input placeholder={t('search.tariffPlaceholder')} value={tariffNumber}
            onChange={(e) => setTariffNumber(e.target.value)} className="h-9" />
        </div>
        <div className="space-y-1 flex-1 min-w-[200px] max-w-[320px]">
          <Label className="text-xs">{t('search.description')}</Label>
          <Input placeholder={t('search.descriptionPlaceholder')} value={description}
            onChange={(e) => setDescription(e.target.value)} className="h-9" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('search.limit')}</Label>
          <Input type="number" value={top} onChange={(e) => setTop(e.target.value)}
            className="w-20 h-9" min="1" max="500" />
        </div>
        <Button type="submit" size="sm" className="h-9">
          <Search className="h-4 w-4 mr-1" /> {t('search.button')}
        </Button>
      </form>

      {/* Error */}
      {error && (
        <div className="border border-destructive/50 bg-destructive/10 rounded-lg p-4">
          <p className="text-sm font-medium text-destructive">{t('search.apiError')}</p>
          <p className="text-xs text-muted-foreground mt-1">{error.message}</p>
          {'data' in error && (error as { data?: unknown }).data != null && (
            <CarmJsonViewer data={(error as { data: unknown }).data} />
          )}
        </div>
      )}

      {/* Results */}
      {hasSearched && (
        <TariffResultsTable data={data?.results || []} totalCount={data?.totalCount ?? null} isLoading={isLoading} />
      )}
      {!hasSearched && (
        <div className="border rounded-lg p-8 text-center text-muted-foreground">
          {t('search.emptyState')}<br />
          <span className="text-xs">{t('search.emptyStateHint')}</span>
        </div>
      )}

      {/* Info Section */}
      <div className="border-t pt-6 mt-8 space-y-4">
        <div className="flex items-center gap-2">
          <Info className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold">{t('info.title')}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-muted-foreground">
          <div className="space-y-3 border rounded-lg p-4">
            <h3 className="font-medium text-foreground">{t('info.whatIsCarm.title')}</h3>
            <p>{t('info.whatIsCarm.p1')}</p>
            <p>{t('info.whatIsCarm.p2')}</p>
          </div>
          <div className="space-y-3 border rounded-lg p-4">
            <h3 className="font-medium text-foreground">{t('info.howToAccess.title')}</h3>
            <ol className="list-decimal ml-4 space-y-1.5">
              <li>{t('info.howToAccess.step1')}{' '}
                <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="text-primary underline inline-flex items-center gap-0.5">
                  {t('info.howToAccess.step1Link')} <ExternalLink className="h-3 w-3" />
                </a>
              </li>
              <li dangerouslySetInnerHTML={{ __html: t('info.howToAccess.step2') }} />
              <li>{t('info.howToAccess.step3')}</li>
              <li>{t('info.howToAccess.step4')}</li>
            </ol>
          </div>
          <div className="space-y-3 border rounded-lg p-4">
            <h3 className="font-medium text-foreground">{t('info.forAtek.title')}</h3>
            <ul className="list-disc ml-4 space-y-1.5">
              <li dangerouslySetInnerHTML={{ __html: t('info.forAtek.item1') }} />
              <li>{t('info.forAtek.item2')}</li>
              <li dangerouslySetInnerHTML={{ __html: t('info.forAtek.item3') }} />
              <li>{t('info.forAtek.item4')}</li>
            </ul>
          </div>
          <div className="space-y-3 border rounded-lg p-4">
            <h3 className="font-medium text-foreground">{t('info.apiAccess.title')}</h3>
            <p>{t('info.apiAccess.p1')}</p>
            <div className="p-2.5 bg-muted/50 rounded-md space-y-1 my-2">
              <p className="font-medium text-foreground">{t('info.apiAccess.atekAccount')}</p>
              <p>{t('info.apiAccess.atekBn')}</p>
              <p>{t('info.apiAccess.atekRm')}</p>
              <p>{t('info.apiAccess.atekLegal')}</p>
            </div>
            <ul className="list-disc ml-4 space-y-1.5">
              <li><strong>{t('info.apiAccess.phone')}</strong> 1-800-461-9999 (press 3, then 3)</li>
              <li><strong>{t('info.apiAccess.email')}</strong> tccu-ustcc@cbsa-asfc.gc.ca</li>
              <li><strong>{t('info.apiAccess.hours')}</strong> {t('info.apiAccess.hoursValue')}</li>
            </ul>
            <p className="mt-2">
              <a href="https://www.cbsa-asfc.gc.ca/services/carm-gcra/menu-eng.html" target="_blank" rel="noopener noreferrer" className="text-primary underline inline-flex items-center gap-0.5">
                {t('info.apiAccess.officialPage')} <ExternalLink className="h-3 w-3" />
              </a>
              {' · '}
              <a href="https://ccp-pcc.cbsa-asfc.cloud-nuage.canada.ca/en/carm-api" target="_blank" rel="noopener noreferrer" className="text-primary underline inline-flex items-center gap-0.5">
                {t('info.apiAccess.apiDocs')} <ExternalLink className="h-3 w-3" />
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Glossary */}
      <div className="border-t pt-6 space-y-4">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold">{t('glossary.title')}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-xs">
          {GLOSSARY_KEYS.map((key) => (
            <div key={key} className="space-y-1">
              <dt className="font-medium text-foreground">{t(`glossary.${key}.term`)}</dt>
              <dd className="text-muted-foreground">{t(`glossary.${key}.def`)}</dd>
            </div>
          ))}
        </div>
      </div>

      {/* Treatment Codes */}
      <div id="treatment-codes" className="border-t pt-6 space-y-4">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold">{t('treatments.title')}</h2>
        </div>
        <p className="text-xs text-muted-foreground" dangerouslySetInnerHTML={{ __html: t('treatments.intro') }} />

        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="text-left px-3 py-2 font-medium w-16">{t('treatments.headers.code')}</th>
                <th className="text-left px-3 py-2 font-medium w-24">{t('treatments.headers.abbreviation')}</th>
                <th className="text-left px-3 py-2 font-medium">{t('treatments.headers.agreement')}</th>
                <th className="text-left px-3 py-2 font-medium">{t('treatments.headers.countries')}</th>
              </tr>
            </thead>
            <tbody>
              {TREATMENT_ROWS.map((tr) => (
                <tr key={tr.code} className={`border-b last:border-0 ${tr.highlight ? 'bg-primary/5' : ''}`}>
                  <td className="px-3 py-2 font-mono font-medium">{tr.code}</td>
                  <td className="px-3 py-2 font-medium">{tr.abbr}</td>
                  <td className="px-3 py-2">{tr.name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{t(`treatments.${tr.code}`)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
          <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
          <div className="text-xs text-blue-800 dark:text-blue-200">
            <p className="font-medium">{t('treatments.atekNote.title')}</p>
            <p className="mt-0.5" dangerouslySetInnerHTML={{ __html: t('treatments.atekNote.text') }} />
          </div>
        </div>
      </div>
    </div>
  );
}
